import React, { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../../../../../context/CompanyRoleAssignmentContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../../context/StockLocationRoleAssignmentContext';
import { UserContext } from '../../../../../../../context/UserContext';
import { StockLocationRole } from '../../../../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { StockLocation } from '../../../../../../../types/stock_location/stockLocation';
import { CancelButton, CreateButton } from '../../../../../../components/Button';
import { DropdownSelect, DropdownSearchSelect } from '../../../../../../components/Dropdown';
import { ActionModal } from '../../../../../../components/Modal';
import {
  StockLocationRoleAssignmentInput,
  StockLocationRoleAssignmentUtil,
} from '../../../../../../../types/stock_location_role_assignment';

import {
  CreateStockLocationRoleAssignmentResponse,
  CreateStockLocationRoleAssignmentVariables,
  StockLocationRoleAssignmentMutation,
} from '../../../../../../../graphql/stockLocationRoleAssignment.graphql';
import { ToastType } from '../../../../../../components/Toast';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { t } from '../../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import useMutation from '../../../../../../../hooks/useMutation';
import { CompanyRole } from '../../../../../../../types/company_role_assignment';

interface CreateStockLocationRoleForStockLocationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  stockLocation: StockLocation;
}

export default function CreateStockLocationRoleForStockLocationModal({
  open,
  setOpen,
  stockLocation,
}: CreateStockLocationRoleForStockLocationModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { companyUsers } = useContext(UserContext);
  const { companyRoles } = useContext(CompanyRoleAssignmentContext);
  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);

  const employeesWithoutAssignment = useMemo(() => {
    const employees = [...companyUsers.values()].filter(u =>
      companyRoles.get(u.id)?.find(r => r.companyId === currentCompany.id && r.role === CompanyRole.employee),
    );
    const userIdsForLocation = new Set((stockLocationRoles.get(stockLocation.id) || []).map(s => s.userId));
    return employees.filter(e => !userIdsForLocation.has(e.id));
  }, [companyUsers, companyRoles]);

  const [input, setInput] = useState(
    new StockLocationRoleAssignmentInput({
      stockLocationId: stockLocation.id,
      companyId: stockLocation.companyId,
      role: StockLocationRole.STOCK_LOCATION_VIEWER,
    }),
  );

  const [create, { loading }] = useMutation<
    CreateStockLocationRoleAssignmentResponse,
    CreateStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.create, {
    variables: { stockLocationRoleAssignments: [input.forCreate()] },
    onCompleted: res => {
      const role = res.createStockLocationRoleAssignment[0];
      const existingRoles = stockLocationRoles.get(role.stockLocationId) || [];
      existingRoles.push(role);
      stockLocationRoles.set(role.stockLocationId, existingRoles);
      setStockLocationRoles(stockLocationRoles);
      setOpen(false);
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleAssignmentCreatedSuccessfully).singular.upper,
      });
    },
  });

  const handleCreate = () => {
    create();
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <CreateButton onClick={handleCreate} loading={loading} disabled={!input.validate()} />
    </div>
  );

  return (
    <ActionModal
      open={open}
      setOpen={setOpen}
      title={t(TranslationKey.assignUserToLocation).singular.upper}
      footer={footer}
    >
      <div className='grid gap-4'>
        <DropdownSearchSelect
          mandatory
          label={t(TranslationKey.user).singular.upper}
          items={employeesWithoutAssignment}
          onSelect={value => setInput(input.withUser(value))}
          value={companyUsers.get(input.userId)}
          placeholder={t(TranslationKey.selectUser).singular.upper}
          textBuilder={item => item.fullName}
        />
        <DropdownSelect
          label={t(TranslationKey.role).singular.upper}
          items={Object.values(StockLocationRole)}
          value={input.role}
          textBuilder={StockLocationRoleAssignmentUtil.roleToString}
          onSelect={value => setInput(input.withRole(value))}
        />
      </div>
    </ActionModal>
  );
}
